import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Header from '../components/Header';
import StatsCard from '../components/StatsCard';
import UserManagement from '../components/UserManagement';
import AdminUserStatement from '../components/AdminUserStatement';
import SupportSystem from '../components/SupportSystem';
import AdminNotifications from '../components/AdminNotifications';
import VirginPoolManagement from '../components/VirginPoolManagement';
import AdminCreditHistory from '../components/AdminCreditHistory';
import AdminTestManagement from '../components/AdminTestManagement';
import AdminPaymentManagement from '../components/AdminPaymentManagement';
import SettingsModal from '../components/SettingsModal';
import supabase from '../supabase/supabase';

const { FiUsers, FiCpu, FiLifeBuoy, FiDatabase, FiSettings, FiDollarSign, FiActivity, FiBell } = FiIcons;

export default function AdminDashboard({ user: initialUser, profile: initialProfile, onLogout }) {
  const [activeTab, setActiveTab] = useState('users');
  const [profile, setProfile] = useState(initialProfile || initialUser);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalTests: 0,
    poolSize: 0,
    activeTickets: 0,
    totalRevenue: 0
  });
  const [selectedUserForStatement, setSelectedUserForStatement] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [refreshCounter, setRefreshCounter] = useState(0);

  const fetchStats = useCallback(async () => {
    try {
      const [users, tests, pool, tickets, payments] = await Promise.all([
        supabase.from('app_profiles').select('id', { count: 'exact' }),
        supabase.from('app_tests').select('id', { count: 'exact' }),
        supabase.from('app_virgin_pool').select('id', { count: 'exact' }).eq('is_assigned', false),
        supabase.from('app_tickets').select('id', { count: 'exact' }).neq('status', 'closed'),
        supabase.from('app_payments_v2').select('amount').eq('status', 'CONFIRMED')
      ]);

      const revenue = payments.data?.reduce((acc, curr) => acc + Number(curr.amount), 0) || 0;

      setStats({
        totalUsers: users.count || 0,
        totalTests: tests.count || 0,
        poolSize: pool.count || 0,
        activeTickets: tickets.count || 0,
        totalRevenue: revenue
      });
    } catch (err) {
      console.error('Error fetching admin stats:', err);
    }
  }, []);

  const fetchCurrentProfile = useCallback(async () => {
    const userId = initialProfile?.id || initialUser?.id;
    if (!userId) return;

    const { data } = await supabase.from('app_profiles').select('*').eq('id', userId).single();
    if (data) setProfile(data);
  }, [initialProfile?.id, initialUser?.id]);

  useEffect(() => {
    fetchStats();
    fetchCurrentProfile();
  }, [fetchStats, fetchCurrentProfile, refreshCounter]);

  const handleRefresh = () => {
    setRefreshCounter(prev => prev + 1);
    fetchCurrentProfile();
  };

  const tabs = [
    { id: 'users', label: 'Usuários', icon: FiUsers },
    { id: 'tests', label: 'Testes', icon: FiActivity },
    { id: 'payments', label: 'Financeiro', icon: FiDollarSign },
    { id: 'stock', label: 'Estoque', icon: FiDatabase },
    { id: 'support', label: 'Suporte', icon: FiLifeBuoy },
    { id: 'notifications', label: 'Notificações', icon: FiBell }
  ];

  return (
    <div className="min-h-screen bg-slate-50 pb-20 sm:pb-0">
      <Header 
        user={profile} 
        profile={profile} 
        onLogout={onLogout} 
        onOpenSettings={() => setShowSettings(true)}
        isAdmin={true}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Mobile Stats Scroll */}
        <div className="flex overflow-x-auto pb-4 gap-4 mb-8 -mx-4 px-4 sm:mx-0 sm:px-0 sm:grid sm:grid-cols-2 lg:grid-cols-4 no-scrollbar">
          <StatsCard 
            title="Usuários" 
            value={stats.totalUsers} 
            icon={FiUsers} 
            color="indigo" 
            className="min-w-[200px] flex-shrink-0"
          />
          <StatsCard 
            title="Faturamento" 
            value={`R$ ${stats.totalRevenue.toFixed(2)}`} 
            icon={FiDollarSign} 
            color="emerald" 
            className="min-w-[200px] flex-shrink-0"
          />
          <StatsCard 
            title="URLs em Estoque" 
            value={stats.poolSize} 
            icon={FiDatabase} 
            color="amber" 
            className="min-w-[200px] flex-shrink-0"
          />
          <StatsCard 
            title="Suporte Pendente" 
            value={stats.activeTickets} 
            icon={FiLifeBuoy} 
            color="red" 
            className="min-w-[200px] flex-shrink-0"
          />
        </div>

        {/* Navigation Tabs - Responsive */}
        <div className="bg-white p-1.5 rounded-2xl border border-slate-200 flex gap-1 mb-8 overflow-x-auto no-scrollbar">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 sm:px-6 py-3 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-100'
                  : 'text-slate-500 hover:bg-slate-50'
              }`}
            >
              <SafeIcon icon={tab.icon} className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content Area */}
        <div className="space-y-8 animate-in fade-in duration-500">
          {activeTab === 'users' && (
            <UserManagement 
              onViewStatement={setSelectedUserForStatement} 
              onRefresh={handleRefresh}
            />
          )}

          {activeTab === 'tests' && (
            <AdminTestManagement />
          )}

          {activeTab === 'payments' && (
            <AdminPaymentManagement />
          )}

          {activeTab === 'stock' && (
            <VirginPoolManagement onRefresh={handleRefresh} />
          )}

          {activeTab === 'support' && (
            <SupportSystem user={profile} isAdmin={true} />
          )}

          {activeTab === 'notifications' && (
            <AdminNotifications />
          )}
        </div>
      </main>

      {/* Modals */}
      <AnimatePresence>
        {selectedUserForStatement && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setSelectedUserForStatement(null)} />
            <motion.div 
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-5xl bg-slate-50 rounded-[2.5rem] shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
            >
              <div className="flex-1 overflow-y-auto p-8">
                <AdminUserStatement
                  userProfile={selectedUserForStatement}
                  onBack={() => setSelectedUserForStatement(null)}
                  onUpdateCredits={handleRefresh}
                />
              </div>
            </motion.div>
          </div>
        )}
        
        {showSettings && (
          <SettingsModal 
            isOpen={showSettings} 
            onClose={() => setShowSettings(false)} 
            profile={profile} 
            onUpdate={handleRefresh}
          />
        )}
      </AnimatePresence>

      {/* Mobile Sticky Tab Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-4 py-2 flex justify-around sm:hidden z-50">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex flex-col items-center p-2 rounded-xl transition-all ${
              activeTab === tab.id ? 'text-indigo-600' : 'text-slate-400'
            }`}
          >
            <SafeIcon icon={tab.icon} className="w-5 h-5" />
            <span className="text-[10px] font-bold mt-1 uppercase tracking-tighter">{tab.label.slice(0, 4)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}